import { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/admin-api';

interface AdminLog {
  id: number;
  admin_id: number;
  action_type: string;
  target_type?: string;
  target_id?: number;
  details?: string;
  created_at: string;
  admin_username?: string;
}

export default function AdminLogsPage() {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  useEffect(() => {
    loadLogs();
  }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getLogs({
        limit,
        offset: (page - 1) * limit,
      });
      setLogs(data.logs);
      setHasMore(data.logs.length === limit);
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'view_user':
        return '👁️';
      case 'ban_user':
        return '🚫';
      case 'unban_user':
        return '✅';
      case 'update_subscription':
        return '💳';
      case 'force_match':
        return '💘';
      case 'cancel_match':
        return '❌';
      case 'resolve_report':
        return '✔️';
      case 'dismiss_report':
        return '➖';
      case 'login':
        return '🔑';
      default:
        return '📝';
    }
  };

  const getActionBadge = (actionType: string) => {
    if (actionType.includes('ban')) {
      return 'badge-error';
    } else if (actionType.includes('match')) {
      return 'badge-primary';
    } else if (actionType.includes('report')) {
      return 'badge-warning';
    } else if (actionType.includes('subscription')) {
      return 'badge-success';
    } else {
      return 'badge-ghost';
    }
  };

  if (loading && page === 1) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Action Logs</h1>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => loadLogs()}
        >
          🔄 Refresh
        </button>
      </div>

      <div className="overflow-x-auto bg-base-100 rounded-box shadow">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Action</th>
              <th>Admin</th>
              <th>Target</th>
              <th>Details</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-base-content/60">
                  No logs found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover">
                  <td className="text-sm text-base-content/60">{log.id}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span>{getActionIcon(log.action_type)}</span>
                      <span className={`badge ${getActionBadge(log.action_type)}`}>
                        {log.action_type}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="font-semibold">
                      {log.admin_username || log.admin_id}
                    </div>
                    <div className="text-xs text-base-content/60">
                      ID: {log.admin_id}
                    </div>
                  </td>
                  <td>
                    {log.target_type && log.target_id ? (
                      <div>
                        <span className="badge badge-sm badge-outline">
                          {log.target_type}
                        </span>
                        <span className="ml-2 text-sm">#{log.target_id}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-base-content/60">-</span>
                    )}
                  </td>
                  <td>
                    <div className="max-w-md">
                      {log.details ? (
                        <details className="collapse collapse-arrow bg-base-200 rounded-box">
                          <summary className="collapse-title text-sm py-2 px-3 cursor-pointer">
                            View details
                          </summary>
                          <div className="collapse-content">
                            <pre className="text-xs overflow-x-auto">
                              {JSON.stringify(JSON.parse(log.details), null, 2)}
                            </pre>
                          </div>
                        </details>
                      ) : (
                        <span className="text-sm text-base-content/60">-</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">
                      {new Date(log.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {new Date(log.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2 mt-6">
        <button
          className="btn btn-sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          ← Previous
        </button>
        <span className="flex items-center px-4 text-sm">
          Page {page}
        </span>
        <button
          className="btn btn-sm"
          onClick={() => setPage((p) => p + 1)}
          disabled={!hasMore}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
